import { ethers } from "ethers"
import { useFerroBarContract, useTokenContract } from "src/hooks/useContract"
import { FER, XFER_GAS_PRICE } from "src/constants"
import { useCallback } from "react"
import { parseUnits } from "ethers/lib/utils"
import { useTransactionModal } from "src/providers/TransactionModalProvider"
import { TransactionSummaryData } from "src/components/TransactionSummary"
import { BIG_ZERO } from "src/constants/bigNumber"

const useFerroBarHandlers = () => {
  const ferroBar = useFerroBarContract()
  const erc20 = useTokenContract(FER)
  const { addTransactions } = useTransactionModal()

  const handleApprove = useCallback(async () => {
    if (ferroBar && erc20) {
      const txn = async () => {
        return await erc20.approve(
          ferroBar.address,
          ethers.constants.MaxUint256,
          {
            gasPrice: XFER_GAS_PRICE,
          },
        )
      }
      const summary = [{ label: "Approve", amount: "", token: FER }]

      await addTransactions([txn], summary)
    }
  }, [addTransactions, erc20, ferroBar])

  const handleEnterPool = useCallback(
    async (amount: string, summary: TransactionSummaryData) => {
      if (ferroBar) {
        const txn = async () => {
          return await ferroBar.enterPool(parseUnits(amount, FER.decimals), {
            gasPrice: XFER_GAS_PRICE,
          })
        }
        await addTransactions([txn], summary, undefined, true)
      }
    },
    [addTransactions, ferroBar],
  )

  const handleEnterPoolStatic = useCallback(
    async (amount: string) => {
      if (ferroBar) {
        const convertedAmount = await ferroBar.callStatic.enterPool(
          parseUnits(amount, FER.decimals),
        )
        return convertedAmount
      }
      return BIG_ZERO
    },
    [ferroBar],
  )

  const handleLeavePool = useCallback(
    async (amount: string, summary: TransactionSummaryData) => {
      if (ferroBar) {
        const txn = async () => {
          return await ferroBar.leavePool(parseUnits(amount, FER.decimals), {
            gasPrice: XFER_GAS_PRICE,
          })
        }
        await addTransactions([txn], summary)
      }
    },
    [addTransactions, ferroBar],
  )

  return {
    onApprove: handleApprove,
    onEnterPool: handleEnterPool,
    onEnterPoolStatic: handleEnterPoolStatic,
    onLeavePool: handleLeavePool,
  }
}

export default useFerroBarHandlers
